import React, { useState, useContext, useEffect, useCallback, Suspense, Dispatch, SetStateAction } from "react";
import { Backdrop, Button, IconButton } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import SyncIcon from "@material-ui/icons/Sync";
import { Tooltip } from "@material-ui/core";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { UIContext } from "../../../../../contexts";

import { ThreeCircles } from "react-loader-spinner";

import "./benefitSummary.css";
import {
	EnrollmentCardGrid,
	LazyEnrollmentCardGrid,
	LazyEnrollmentMapSection,
	LazyEnrollmentTabSection
} from "../../..";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { GROUP_NUMBER } from "../../../../../constants/groups";
import EnrollmentCardGridPFB from "./enrollmentCardGridPFB/EnrollmentCardGrid";
import EnrollmentTabSectionPFB from "./enrollmentTabSectionPFB/EnrollmentTabSection";
import EnrollmentCardGridPL from "./enrollmentCardGridPL/EnrollmentCardGrid";
import EnrollmentTabSectionPL from "./enrollmentTabSectionPL/EnrollmentTabSection";
import EnrollmentMapSectionPL from "./enrollmentMapsSectionPL/EnrollmentMapsSectionPl";
import EnrollmentMapSectionPFB from "./enrollmentMapsSectionPFB/EnrollmentMapsSectionPFB";
import EnrollmentMapSectionV2 from "./enrollmentMapSection/EnrollmentMapSectionV2";
import EnrollmentTabSectionV2 from "./enrollmentTabSection/EnrollmentTabSectionV2";

const backdropStyles = makeStyles((theme: Theme) =>
	createStyles({
		backdrop: {
			zIndex: theme.zIndex.drawer + 1,
			color: "#fff",
			position: "absolute"
		}
	})
);

// ======================================================================================================
// Single source of truth for which set of Card/Tab/Map components a group's Benefit Summary renders.
// Adding a new per-group variant means adding one entry here — not touching three separate ternary
// chains in the JSX below.
// ======================================================================================================
type RefreshProps = {
	refresh: boolean;
	setRefresh: Dispatch<SetStateAction<boolean>>;
};

type BenefitSummaryVariant = {
	CardGrid: React.ComponentType<RefreshProps>;
	TabSection: React.ComponentType<RefreshProps>;
	MapSection: React.ComponentType<RefreshProps>;
};

// NOTE: deliberately NOT built as module-level constants. EnrollmentCardGrid/EnrollmentTabSection/
// EnrollmentMapSection come from the barrel (`../../..`), which forms a circular import back through
// this file's directory tree. Reading them eagerly at module-evaluation time (rather than deferring to
// render, like JSX does) can observe them as `undefined` before the barrel finishes initializing —
// especially under webpack HMR. Building the lookup inside the component keeps the same deferred-access
// timing the original ternary-in-JSX code had.
const getDefaultBenefitSummaryVariant = (): BenefitSummaryVariant => ({
	CardGrid: EnrollmentCardGrid,
	TabSection: EnrollmentTabSectionV2,
	MapSection: EnrollmentMapSectionV2
});

const getBenefitSummaryVariantsByGroup = (): Record<number, BenefitSummaryVariant> => ({
	[GROUP_NUMBER.PANHANDLE_FIRST_BANK]: {
		CardGrid: EnrollmentCardGridPFB,
		TabSection: EnrollmentTabSectionPFB,
		MapSection: EnrollmentMapSectionPFB
	},
	[GROUP_NUMBER.PROGRESSIVE_LABORATORIES]: {
		CardGrid: EnrollmentCardGridPL,
		TabSection: EnrollmentTabSectionPL,
		MapSection: EnrollmentMapSectionPL
	}
});

const BenefitSummary: React.FC = () => {
	const [pageLoaderState, setPageLoaderState] = useState<boolean>(false);
	const [refresh, setRefresh] = useState<boolean>(false);
	const { setDashboardHeader } = useContext(UIContext);
	const queryGroupNumber = useRouteParam("gn");
	const backdropClasses = backdropStyles();

	const handleRefreshClick = useCallback(() => {
		setRefresh(true);
		setPageLoaderState(true);

		setTimeout(() => {
			setPageLoaderState(false);
		}, 1500);
	}, []);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employer_dashboard);
	}, [setDashboardHeader]);

	const { CardGrid, TabSection, MapSection } =
		(queryGroupNumber && getBenefitSummaryVariantsByGroup()[queryGroupNumber as number]) ||
		getDefaultBenefitSummaryVariant();

	return (
		<div className="benefit-summary-container">
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Tooltip title="Refresh Dashboard" enterDelay={200} leaveDelay={200} arrow>
					<IconButton onClick={handleRefreshClick}>
						<SyncIcon className="button-icon-refresh" />
					</IconButton>
				</Tooltip>
			</div>
			<Suspense fallback={<div />}>
				<CardGrid refresh={refresh} setRefresh={setRefresh} />
			</Suspense>
			<Suspense fallback={<div />}>
				<TabSection refresh={refresh} setRefresh={setRefresh} />
			</Suspense>
			<Suspense fallback={<div />}>
				<MapSection refresh={refresh} setRefresh={setRefresh} />
			</Suspense>
			<Backdrop className={backdropClasses.backdrop} open={pageLoaderState}>
				<ThreeCircles
					height="100"
					width="100"
					color="#4fa94d"
					wrapperStyle={{}}
					wrapperClass=""
					visible={true}
					ariaLabel="three-circles-rotating"
					outerCircleColor=""
					innerCircleColor=""
					middleCircleColor=""
				/>
			</Backdrop>
		</div>
	);
};

export default BenefitSummary;
